import React, { useCallback, useEffect, useState } from "react";
import { modulesAPI } from "../services/api";
import { useMembership } from "../context/MembershipContext";
import { useI18n } from "../i18n";

const PRESETS = ["counter", "profit", "mixing", "full"];
const QUESTIONS = ["credit_sales", "preorders", "offers", "packs", "cash_drawer", "customer_menu"];

/**
 * The preset that matches what is on right now, for highlighting. Only the
 * profit/mixing axis counts: the yes/no questions are separate.
 */
const currentPreset = (mods) => {
  const on = (key) => mods[key]?.enabled;
  const offeredEverything = Object.values(mods).every((mod) => mod.enabled || !mod.offered);

  if (on("mixing")) return offeredEverything ? "full" : "mixing";
  if (on("finance")) return offeredEverything ? "full" : "profit";
  return "counter";
};

/**
 * Presets in plain words, a few yes/no questions, and — folded away — every
 * switch. Each change is saved as it is made; the server is the authority, so
 * the screen redraws from what it answers (a switch that needs another brings it
 * along, and one the plan does not offer is refused).
 *
 * Used on its own page and, later, as a step of the first-run wizard.
 */
const ModulesChooser = ({ onSaved, compact = false }) => {
  const { t } = useI18n();
  const { refresh } = useMembership() || {};
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ kind: "", text: "" });

  useEffect(() => {
    modulesAPI
      .get()
      .then(({ data }) => setState(data))
      .catch(() => setMessage({ kind: "error", text: t("modules.loadFailed") }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = useCallback(
    async (payload, note) => {
      setBusy(true);
      setMessage({ kind: "", text: "" });

      try {
        const { data } = await modulesAPI.update(payload);
        setState(data);
        setMessage({ kind: "ok", text: note || t("modules.saved") });
        // The sidebar and every screen read what is on from the shop
        refresh?.();
        onSaved?.(data);
      } catch (err) {
        const body = err.response?.data;
        setMessage({
          kind: "error",
          text: body?.code === "MODULE_NOT_OFFERED" ? t("modules.notOffered") : t("modules.saveFailed"),
        });
      } finally {
        setBusy(false);
      }
    },
    [onSaved, refresh, t],
  );

  if (!state) {
    return message.text ? <p className="text-red-600">{message.text}</p> : null;
  }

  const mods = Object.fromEntries(state.modules.map((mod) => [mod.key, mod]));
  const names = (keys) => keys.map((key) => t(`modules.items.${key}.name`)).join(", ");

  // What a switch will bring along, said before it is pressed
  const consequence = (mod, turningOn) => {
    if (turningOn) {
      const needed = mod.requires.filter((key) => !mods[key].enabled);
      return needed.length ? t("modules.alsoTurnsOn", { names: names(needed) }) : "";
    }
    const going = mod.dependents.filter((key) => mods[key].enabled);
    return going.length ? t("modules.alsoTurnsOff", { names: names(going) }) : "";
  };

  const toggle = (mod) => {
    const turningOn = !mod.enabled;
    save({ modules: { [mod.key]: turningOn } }, consequence(mod, turningOn) || undefined);
  };

  const renderSwitch = (mod) => {
    const reason = !mod.offered
      ? t("modules.notInPlan")
      : mod.enabled
        ? ""
        : !mod.owner_off
          ? t("modules.alsoOff", { names: names(mod.requires.filter((key) => !mods[key].enabled)) })
          : "";

    return (
      <label
        key={mod.key}
        className={`flex items-start gap-3 rounded-lg border p-3 ${
          mod.offered ? "border-gray-200 bg-white" : "border-gray-200 bg-gray-50 opacity-70"
        }`}
      >
        <input
          type="checkbox"
          role="switch"
          checked={mod.enabled}
          disabled={busy || !mod.offered}
          onChange={() => toggle(mod)}
          className="mt-1 h-6 w-6 shrink-0"
        />
        <span className="min-w-0">
          <span className="block font-medium text-gray-800">{t(`modules.items.${mod.key}.name`)}</span>
          <span className="block text-sm text-gray-500">{t(`modules.items.${mod.key}.desc`)}</span>
          {mod.requires.length > 0 && (
            <span className="block text-xs text-gray-400">
              {t("modules.needs", { names: names(mod.requires) })}
            </span>
          )}
          {reason && <span className="block text-xs text-amber-700">{reason}</span>}
        </span>
      </label>
    );
  };

  const active = currentPreset(mods);

  return (
    <div className="space-y-6">
      {!compact && <p className="text-gray-600">{t("modules.intro")}</p>}

      <section>
        {!compact && <h2 className="mb-3 text-lg font-semibold text-gray-800">{t("modules.howMuch")}</h2>}
        <div className="grid gap-3 sm:grid-cols-2">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              disabled={busy}
              onClick={() => save({ preset })}
              aria-pressed={active === preset}
              className={`rounded-xl border-2 p-4 text-start transition-colors disabled:opacity-60 ${
                active === preset
                  ? "border-primary-600 bg-primary-50"
                  : "border-gray-200 bg-white hover:border-primary-300"
              }`}
            >
              <span className="block font-semibold text-gray-800">{t(`modules.presets.${preset}.name`)}</span>
              <span className="block text-sm text-gray-600">{t(`modules.presets.${preset}.desc`)}</span>
            </button>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold text-gray-800">{t("modules.questions")}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {QUESTIONS.map((key) => renderSwitch(mods[key]))}
        </div>
      </section>

      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer font-semibold text-gray-800">{t("modules.advanced")}</summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {state.modules.map((mod) => renderSwitch(mod))}
        </div>
      </details>

      <p
        role="status"
        aria-live="polite"
        className={`min-h-[1.5rem] text-sm ${message.kind === "error" ? "text-red-600" : "text-green-700"}`}
      >
        {busy ? t("modules.saving") : message.text}
      </p>
    </div>
  );
};

export default ModulesChooser;
